import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex text-p-2 items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-white transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-neutral-blue-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-neutral-blue-950 dark:focus-visible:ring-neutral-blue-300',
  {
    variants: {
      variant: {
        default:
          'rounded-sm border border-neutral-blue-900 bg-neutral-blue-900 text-neutral-blue-50 hover:bg-neutral-blue-900/90 dark:bg-neutral-blue-50 dark:text-neutral-blue-900 dark:hover:bg-neutral-blue-50/90',
        primary:
          'rounded-sm border border-primary bg-primary text-shades-0 hover:bg-primary/90',
        destructive:
          'rounded-sm bg-red-500 text-neutral-blue-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-neutral-blue-50 dark:hover:bg-red-900/90 focus:ring-0! focus:ring-offset-0! ring-0! ring-offset-0!',
        outline:
          'rounded-sm border border-neutral-blue-200 bg-white hover:bg-neutral-blue-100 hover:text-neutral-blue-900 dark:border-slate-800 dark:bg-neutral-blue-800 dark:hover:bg-neutral-blue-700 dark:hover:text-neutral-blue-50',
        secondary:
          'rounded-sm bg-neutral-blue-100 text-neutral-blue-900 hover:bg-neutral-blue-100/80 dark:bg-neutral-blue-800 dark:text-neutral-blue-50 dark:hover:bg-neutral-blue-800/80',
        ghost:
          'rounded-sm hover:bg-neutral-blue-100 hover:text-neutral-blue-900 dark:hover:bg-neutral-blue-800 dark:hover:text-neutral-blue-50',
        link: 'text-neutral-blue-900 underline-offset-4 hover:underline dark:text-neutral-blue-50',
        success:
          'rounded-sm bg-success-500 text-shades-100 hover:bg-success-500/70',
        underline: 'p-0! underline text-neutral-blue-300 hover:text-shades-0',
      },
      size: {
        default: 'rounded-sm py-3 px-6',
        sm: 'rounded-sm h-9 px-3',
        lg: 'rounded-md h-11 px-8',
        icon: 'h-10 w-10 rounded-full p-0',
        square: 'h-10 w-10 rounded-sm p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
